import type React from 'react';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';
import type { TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';

export type ControlledTextFieldProps<TFields extends FieldValues> = React.PropsWithChildren<
  UseControllerProps<TFields> &
    TextFieldProps & {
      onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
      onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    }
>;

export function ControlledTextField<TFields extends FieldValues>({
  label,
  name,
  control,
  rules,
  InputProps,
  onChange,
  onBlur,
  helperText,
  ...props
}: ControlledTextFieldProps<TFields>) {
  const {
    field: { ref, onBlur: onBlurField, onChange: onChangeField, value, ...fields },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const handleOnBlur: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) => {
    onBlurField();
    onBlur?.(e);
  };

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    e.target.value = value;
    onChangeField(e);
    onChange?.(e);
  };

  return (
    <TextField
      fullWidth
      label={label}
      value={value ?? ''}
      variant="outlined"
      inputRef={ref}
      helperText={helperText || error?.message}
      InputProps={{
        ...InputProps,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        sx: {
          ...props.sx,
          '& .MuiInputBase-input': {
            flex: 1,
          },
        },
      }}
      id={`mui-component-textfield-${name}`}
      error={!!error}
      {...fields}
      {...props}
    />
  );
}
